<template>
  <div class="statistics-dashboard">
    <div class="row mb-4">
      <div class="col-12">
        <h2>{{ $t('statistics.title') }}</h2>
        <p class="text-muted">{{ $t('statistics.subtitle') }}</p>
      </div>
    </div>

    <!-- Date Range Filter -->
    <div class="row mb-4">
      <div class="col-md-6">
        <label>{{ $t('statistics.dateRange') }}</label>
        <div class="input-group">
          <input type="date" class="form-control" v-model="dateRange.start" @change="updateStatistics">
          <div class="input-group-append">
            <span class="input-group-text">{{ $t('statistics.to') }}</span>
          </div>
          <input type="date" class="form-control" v-model="dateRange.end" @change="updateStatistics">
        </div>
      </div>
      <div class="col-md-6">
        <label>{{ $t('statistics.experience') }}</label>
        <select class="form-control" v-model="selectedExperience" @change="updateStatistics">
          <option value="">{{ $t('statistics.allExperiences') }}</option>
          <option v-for="experience in experiences" :key="experience.id" :value="experience.id">
            {{ experience.name || experience.title || 'Experiência ' + experience.id }}
          </option>
        </select>
      </div>
    </div>

    <!-- Key Metrics Cards -->
    <div class="row mb-4">
      <div class="col-md-3">
        <div class="card bg-primary text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h4>{{ statistics.totalReservations }}</h4>
                <p class="mb-0">{{ $t('statistics.totalReservations') }}</p>
              </div>
              <div class="align-self-center">
                <i class="fas fa-calendar-check fa-2x"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card bg-success text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h4>{{ statistics.confirmedReservations }}</h4>
                <p class="mb-0">{{ $t('statistics.confirmedReservations') }}</p>
              </div>
              <div class="align-self-center">
                <i class="fas fa-check-circle fa-2x"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card bg-info text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h4>{{ statistics.totalPeople }}</h4>
                <p class="mb-0">{{ $t('statistics.totalPeople') }}</p>
              </div>
              <div class="align-self-center">
                <i class="fas fa-users fa-2x"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card bg-warning text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h4>{{ statistics.revenue }}</h4>
                <p class="mb-0">{{ $t('statistics.revenue') }}</p>
              </div>
              <div class="align-self-center">
                <i class="fas fa-euro-sign fa-2x"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistics Summary Row (no charts) -->
    <div class="row mb-4">
      <!-- Reservations by Status -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h5>{{ $t('statistics.reservationsByStatus') }}</h5>
          </div>
          <div class="card-body">
            <div class="row text-center">
              <div class="col-3">
                <div class="border rounded p-3">
                  <h4 class="text-warning">{{ statusCounts.pending }}</h4>
                  <small class="text-muted">Pendente</small>
          </div>
        </div>
              <div class="col-3">
                <div class="border rounded p-3">
                  <h4 class="text-success">{{ statusCounts.confirmed }}</h4>
                  <small class="text-muted">Confirmada</small>
      </div>
          </div>
              <div class="col-3">
                <div class="border rounded p-3">
                  <h4 class="text-danger">{{ statusCounts.cancelled }}</h4>
                  <small class="text-muted">Cancelada</small>
          </div>
        </div>
              <div class="col-3">
                <div class="border rounded p-3">
                  <h4 class="text-secondary">{{ statusCounts.completed }}</h4>
                  <small class="text-muted">Completa</small>
      </div>
    </div>
          </div>
          </div>
        </div>
      </div>
      
      <!-- Guide Performance -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h5>{{ $t('statistics.guidePerformance') }}</h5>
          </div>
          <div class="card-body">
            <div v-for="guide in guideStats" :key="guide.id" class="guide-stat mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{{ guide.name }}</strong>
                  <br>
                  <small class="text-muted">{{ guide.reservations }} {{ $t('statistics.reservations') }}</small>
                </div>
                <div class="text-right">
                  <span class="badge badge-primary">{{ guide.rating }}%</span>
                </div>
              </div>
              <div class="progress mt-2" style="height: 4px;">
                <div class="progress-bar" :style="{ width: guide.rating + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Detailed Tables -->
    <div class="row">
      <!-- Top Experiences -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h5>{{ $t('statistics.topExperiences') }}</h5>
          </div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>{{ $t('statistics.experience') }}</th>
                    <th>{{ $t('statistics.reservations') }}</th>
                    <th>{{ $t('statistics.people') }}</th>
                    <th>{{ $t('statistics.revenue') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="exp in topExperiences" :key="exp.id">
                    <td>{{ exp.name }}</td>
                    <td>{{ exp.reservations }}</td>
                    <td>{{ exp.people }}</td>
                    <td>€{{ exp.revenue }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Recent Reservations -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h5>{{ $t('statistics.recentReservations') }}</h5>
          </div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>{{ $t('statistics.tourist') }}</th>
                    <th>{{ $t('statistics.experience') }}</th>
                    <th>{{ $t('statistics.date') }}</th>
                    <th>{{ $t('statistics.status') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="reservation in recentReservations" :key="reservation.id">
                    <td>{{ reservation.touristName }}</td>
                    <td>{{ reservation.experienceName }}</td>
                    <td>{{ formatDate(reservation.date) }}</td>
                    <td>
                      <span class="badge" :class="`badge-${getStatusClass(reservation.status)}`">
                        {{ getStatusText(reservation.status) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
 import { mapState, mapActions } from 'vuex';

export default {
  name: 'StatisticsDashboard',
  data() {
    return {
      loading: false,
      dateRange: {
        start: this.getDate30DaysAgo(),
        end: this.getTodayDate()
      },
      selectedExperience: '',
      statistics: {
        totalReservations: 0,
        confirmedReservations: 0,
        totalPeople: 0,
        revenue: 0
      },
      experiences: [],
      guideStats: [],
      topExperiences: [],
      recentReservations: [],
      statusCounts: {
        pending: 0,
        confirmed: 0,
        cancelled: 0,
        completed: 0
      }
    }
  },
  computed: {
    ...mapState('experiences', ['getExperiencesOutput']),
    ...mapState('reservations', ['getReservationsOutput'])
  },
  watch: {
    getExperiencesOutput: {
      handler(newVal) {
        if (newVal && newVal.data && this.experiences.length === 0) {
          this.experiences = newVal.data;
        }
      },
      immediate: true
    }
  },
  async mounted() {
    await this.loadExperiences();
    await this.loadStatistics();
  },
  methods: {
    ...mapActions('experiences', ['getExperiences']),
    ...mapActions('reservations', ['getReservations']),
    
    getExperienceDisplayName(experienceId) {
      const experience = this.experiences.find(exp => exp.id === experienceId);
      return experience ? (experience.name || experience.title || 'Experiência ' + experienceId) : 'N/A';
    },
    
    async loadExperiences() {
      try {
        const input = { 
          pageSize: 100, 
          pageNumber: 1,
          propertyName: 'name'
        };
        const response = await this.getExperiences(input);
        
        // Try different ways to access the data
        let experiencesData = [];
        if (response && response.data) {
          experiencesData = response.data;
        } else if (response && Array.isArray(response)) {
          experiencesData = response;
        } else if (this.getExperiencesOutput && this.getExperiencesOutput.data) {
          experiencesData = this.getExperiencesOutput.data;
        } else if (this.getExperiencesOutput && Array.isArray(this.getExperiencesOutput)) {
          experiencesData = this.getExperiencesOutput;
        } else if (this.experiences && this.experiences.length > 0) {
          experiencesData = this.experiences;
        }
        
        this.experiences = experiencesData;
        
        // If no experiences loaded, try to reload after a short delay
        if (this.experiences.length === 0) {
          setTimeout(() => {
            this.loadExperiences();
          }, 1000);
        }
      } catch (error) {
        console.error('Error loading experiences for statistics:', error);
      }
    },
    
    
    async loadStatistics() {
      try {
        this.loading = true;
        
        // Load reservations for the selected date range
        const input = {
          pageSize: 1000,
          pageNumber: 1,
          propertyName: 'reservationDate'
        };
        
        // Only add filters if they have values and are valid dates
        if (this.dateRange.start && this.dateRange.start !== '') {
          input.startDate = this.dateRange.start;
        }
        if (this.dateRange.end && this.dateRange.end !== '') {
          input.endDate = this.dateRange.end;
        }
        if (this.selectedExperience) {
          input.experienceId = this.selectedExperience;
        }
        
        await this.getReservations(input);
        let reservations = this.getReservationsOutput?.data || [];
        
        // Apply local date filtering if dates are selected
        if (this.dateRange.start && this.dateRange.end) {
          const startDate = new Date(this.dateRange.start);
          const endDate = new Date(this.dateRange.end);
          endDate.setHours(23, 59, 59, 999); // Include the entire end date
          
          reservations = reservations.filter(reservation => {
            const reservationDate = new Date(reservation.reservationDate || reservation.date || reservation.createdAt);
            return reservationDate >= startDate && reservationDate <= endDate;
          });
        }
        
        // Calculate statistics
        this.calculateStatistics(reservations);
        this.generateRealData(reservations);
        
      } catch (error) {
        console.error('Error loading statistics:', error);
      } finally {
        this.loading = false;
      }
    },
    
    calculateStatistics(reservations) {
      this.statistics.totalReservations = reservations.length;
      this.statistics.confirmedReservations = reservations.filter(r => 
        r.status === 'Confirmed' || r.status === 'confirmed'
      ).length;
      this.statistics.totalPeople = reservations.reduce((sum, r) => {
        let peopleCount = 0;
        if (r.totalPeople && typeof r.totalPeople === 'number') {
          peopleCount = r.totalPeople;
        } else if (r.numberOfPeople && typeof r.numberOfPeople === 'number') {
          peopleCount = r.numberOfPeople;
        } else if (Array.isArray(r.people)) {
          peopleCount = r.people.length;
        } else if (r.reservationPeople && Array.isArray(r.reservationPeople)) {
          peopleCount = r.reservationPeople.length;
        } else if (r.people && typeof r.people === 'number') {
          peopleCount = r.people;
        }
        return sum + peopleCount;
      }, 0);
      this.statistics.revenue = reservations.reduce((sum, r) => 
        sum + (r.prePaymentAmount || r.totalAmount || r.amount || 0), 0
      );
      
      // Calculate status counts for summary cards
      this.statusCounts = { pending: 0, confirmed: 0, cancelled: 0, completed: 0 };
      reservations.forEach(r => {
        const s = (r.status || 'Pending').toLowerCase();
        if (s === 'confirmed') this.statusCounts.confirmed++;
        else if (s === 'cancelled') this.statusCounts.cancelled++;
        else if (s === 'completed') this.statusCounts.completed++;
        else this.statusCounts.pending++;
      });
    },
    
    generateRealData(reservations) {
      // Generate real data from reservations
      this.generateGuideStats(reservations);
      this.generateTopExperiences(reservations);
      this.generateRecentReservations(reservations);
    },
    
    generateGuideStats(reservations) {
      const guideMap = new Map();
      
      reservations.forEach(reservation => {
        if (reservation.experienceAvailability && reservation.experienceAvailability.guide) {
          const guide = reservation.experienceAvailability.guide;
          if (!guideMap.has(guide.id)) {
            guideMap.set(guide.id, {
              id: guide.id,
              name: guide.name,
              reservations: 0,
              totalPeople: 0
            });
          }
          const guideStat = guideMap.get(guide.id);
          guideStat.reservations++;
          guideStat.totalPeople += reservation.totalPeople || reservation.numberOfPeople || 0;
        }
      });
      
      this.guideStats = Array.from(guideMap.values()).map(guide => ({
        ...guide,
        rating: Math.min(95, 70 + (guide.reservations * 2)) // Simple rating calculation
      }));
    },
    
    generateTopExperiences(reservations) {
      const experienceMap = new Map();
      
      reservations.forEach(reservation => {
        if (reservation.experience) {
          const exp = reservation.experience;
          if (!experienceMap.has(exp.id)) {
            experienceMap.set(exp.id, {
              id: exp.id,
              name: this.getExperienceDisplayName(exp.id), // Use helper method for correct name
              reservations: 0,
              people: 0,
              revenue: 0
            });
          }
          const expStat = experienceMap.get(exp.id);
          expStat.reservations++;
          
          // Count people from multiple possible sources
          let peopleCount = 0;
          if (reservation.totalPeople && typeof reservation.totalPeople === 'number') {
            peopleCount = reservation.totalPeople;
          } else if (reservation.numberOfPeople && typeof reservation.numberOfPeople === 'number') {
            peopleCount = reservation.numberOfPeople;
          } else if (Array.isArray(reservation.people)) {
            peopleCount = reservation.people.length;
          } else if (reservation.reservationPeople && Array.isArray(reservation.reservationPeople)) {
            peopleCount = reservation.reservationPeople.length;
          } else if (reservation.people && typeof reservation.people === 'number') {
            peopleCount = reservation.people;
          }
          
          expStat.people += peopleCount;
          expStat.revenue += reservation.prePaymentAmount || reservation.totalAmount || reservation.amount || 0;
        }
      });
      
      this.topExperiences = Array.from(experienceMap.values())
        .sort((a, b) => b.reservations - a.reservations)
        .slice(0, 5);
    },
    
    generateRecentReservations(reservations) {
      this.recentReservations = reservations
        .sort((a, b) => new Date(b.reservationDate || b.date || b.createdAt) - new Date(a.reservationDate || a.date || a.createdAt))
        .slice(0, 10)
        .map(reservation => ({
          id: reservation.id,
          touristName: reservation.touristName || reservation.tourist?.name || 'N/A',
          experienceName: this.resolveExperienceName(reservation),
          date: reservation.reservationDate || reservation.date || reservation.createdAt,
          status: reservation.status
        }));
    },
    
    resolveExperienceName(reservation) {
      // Try common locations first
      const direct = reservation.experience?.name || reservation.experience?.title;
      if (direct) return direct;
      const fromAvailability = reservation.experienceAvailability?.experience?.name || reservation.experienceAvailability?.experience?.title;
      if (fromAvailability) return fromAvailability;
      // Try resolving by id using the loaded experiences list
      const expId = reservation.experienceId || reservation.experience?.id || reservation.experienceAvailability?.experienceId;
      if (expId && Array.isArray(this.experiences) && this.experiences.length > 0) {
        const found = this.experiences.find(e => e.id === expId);
        if (found) return found.name || found.title || `Experiência ${expId}`;
      }
      return 'N/A';
    },
    
    // charts removed - summary cards are updated via calculateStatistics
    
    updateStatistics() {
      this.loadStatistics();
    },
    
    getDate30DaysAgo() {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      return date.toISOString().split('T')[0];
    },
    
    getTodayDate() {
      return new Date().toISOString().split('T')[0];
    },
    
    formatDate(date) {
      return new Date(date).toLocaleDateString(this.$i18n.locale);
    },
    
    getStatusClass(status) {
      switch (status) {
        case 'Pending': return 'warning';
        case 'Confirmed': return 'success';
        case 'Cancelled': return 'danger';
        case 'Completed': return 'secondary';
        default: return 'secondary';
      }
    },
    
    getStatusText(status) {
      return this.$t(`statistics.status.${status.toLowerCase()}`);
    },
    
    
  }
}
</script>

<style scoped>
.statistics-dashboard {
  padding: 1rem;
}

.card {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border: none;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}



.guide-stat {
  padding: 0.5rem;
  border-radius: 0.375rem;
  background-color: #f8f9fa;
}

.table th {
  border-top: none;
  font-weight: 600;
  color: #495057;
}

.badge {
  font-size: 0.75rem;
}

@media (max-width: 768px) {
  .statistics-dashboard {
    padding: 0.5rem;
  }
  
  .card-body canvas {
    max-height: 200px;
  }
}
</style>

